import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

/**
 * CardWidget - Renders a single KPI value with customizable styling
 *
 * Metadata Configuration:
 * - borderColor: Color of the left border (e.g. "#4caf50")
 * - valueColor: Color of the value text (e.g. "#03a9f4")
 *
 * @param {Array} data - Array of data points (uses the first one)
 * @param {Object} config - Widget configuration
 */
export default function CardWidget({ data, config, height = 300 }) {
  const { value, title, borderColor, valueColor } = useMemo(() => {
    // Default values
    let val = 0;
    let titleText = config.title; // Default to widget title
    let bColor = "#1976d2"; // Default primary blue
    let vColor = "#1976d2";

    if (data && data.length > 0) {
      // Use first data point
      const item = data[0];
      val = parseFloat(item.yValue || 0);

      // If xLabel is present and meaningful (not 'Total'), maybe append or use it?
      // For now, sticking to config.title as the main label is safer for "Cards"
      // unless specific requirement to use data label.

      // Parse metadata for styling and value overrides
      if (item.metadata) {
        try {
          const meta = JSON.parse(item.metadata);
          if (meta.borderColor) bColor = meta.borderColor;
          if (meta.valueColor) vColor = meta.valueColor;

          // Allow text values override
          if (meta.textValue) {
            val = meta.textValue;
          } else {
            if (meta.format && meta.format === "text") {
              val = item.xLabel;
            }
          }
        } catch (e) {
          console.warn(
            "Failed to parse metadata for CardWidget",
            config.widgetCode,
            e
          );
        }
      }
    }

    return {
      value: val,
      title: titleText,
      borderColor: bColor,
      valueColor: vColor,
    };
  }, [data, config]);

  // Helper to format value
  const formatValue = (val, metaStr) => {
    if (typeof val !== "number") return val;

    let format = "number";
    let fractionDigits = undefined;

    if (metaStr) {
      try {
        const meta = JSON.parse(metaStr);
        if (meta.format) format = meta.format;
        if (meta.fractionDigits !== undefined)
          fractionDigits = meta.fractionDigits;
      } catch (e) {}
    }

    const options = {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    };

    switch (format) {
      case "currency":
        return new Intl.NumberFormat("en-US", {
          ...options,
          style: "currency",
          currency: "USD",
        }).format(val);
      case "percent":
        // Assuming data is 0.5 for 50%, or user might send 50.
        // Standard is 0.5 -> 50%. If user sends 50, they should likely just use suffix.
        // Let's assume standard decimal percent.
        return new Intl.NumberFormat("en-US", {
          ...options,
          style: "percent",
        }).format(val);
      case "decimal":
        return new Intl.NumberFormat("en-US", {
          ...options,
          style: "decimal",
        }).format(val);
      default:
        return val.toLocaleString("en-US", options);
    }
  };

  const displayValue = useMemo(() => {
    // If explicit text override exists, use it
    if (typeof value === "string") return value;

    // Otherwise format the number
    const itemMetadata = data && data.length > 0 ? data[0].metadata : null;
    return formatValue(value, itemMetadata);
  }, [value, data]);

  // Parse widget definition metadata for styling (e.g. fontSize)
  const widgetStyle = useMemo(() => {
    let style = { fontSize: "2rem" }; // Default size
    if (config?.metadata) {
      try {
        const meta = JSON.parse(config.metadata);
        if (meta.fontSize) style.fontSize = meta.fontSize;
        if (meta.textAlign) style.textAlign = meta.textAlign;
      } catch (e) {}
    }
    return style;
  }, [config]);

  return (
    <Box
      sx={{
        height: height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderLeft: `5px solid ${borderColor}`,
        pl: 2,
      }}
    >
      <Typography
        variant="h3"
        component="div"
        sx={{
          color: valueColor,
          fontWeight: "bold",
          fontSize: widgetStyle.fontSize,
          textAlign: widgetStyle.textAlign || "left",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}
